import {HideableValue, LocationCoordinates, OmitStrict, RichTextContent, Values} from '@wix/devzai-utils-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import type {DoppeBymoPageVideoSource} from './doppe-bymo-page-video-source';
import type {BymoPageActionMediaViewSettings} from '../bymo-pages';
import type {
    DoppeActionTypeTestimonialsList
} from '../../doppe-action-types/doppe-action-type-testimonials-list/doppe-action-type-testimonials-list';
import type {
    DoppeActionTypeMediaGallery
} from '../../doppe-action-types/doppe-action-type-media-gallery/doppe-action-type-media-gallery';
import type {DoppeListActionSettings} from './doppe-list-action-settings';
import type {BymoPagePropsContentSettingsMetadata} from "../bymo-page-props-metadata/bymo-page-props-metadata";
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata
} from "../bymo-page-props-metadata/bymo-page-props-metadata";

/**
 * The types of content sections that can be used in any action that supports content sections.
 */
export const DoppeActionCommonContentSectionTypes = {
    RichContent: 'RichContent',
    Image: 'Image',
    Video: 'Video',
    Gallery: 'Gallery',
    Testimonials: 'Testimonials',
    Map: 'Map',
} as const;

export type DoppeActionCommonContentSectionTypes = Values<typeof DoppeActionCommonContentSectionTypes>;

/**
 * The types of content sections that can be used in specific actions that support content sections.
 */
export const DoppeActionTypeSpecificContentSectionTypes = {
    PropertyDetails: 'PropertyDetails',
} as const;

export type DoppeActionTypeSpecificContentSectionTypes = Values<typeof DoppeActionTypeSpecificContentSectionTypes>;

export const DoppeActionContentSectionType = {
    ...DoppeActionCommonContentSectionTypes,
    ...DoppeActionTypeSpecificContentSectionTypes,
} as const;

export type DoppeActionContentSectionType = Values<typeof DoppeActionContentSectionType>;


export type DoppeActionContentSection = DoppeActionContentSection.Section;

export type DoppeActionContentSectionContent =
    | DoppeActionContentSection.RichTextSettings
    | DoppeActionContentSection.ImageSettings
    | DoppeActionContentSection.VideoSettings
    | DoppeActionContentSection.GallerySettings
    | DoppeActionContentSection.TestimonialsSettings
    | DoppeActionContentSection.PropertyDetailsSettings
    | DoppeActionContentSection.MapSettings;


export namespace DoppeActionContentSection {

    export type BaseType = {
        content: DoppeActionContentSectionContent;
    }

    export type Common = DoppeActionContentSection.BaseType & {
        isVisible: boolean;
    }

    export type Section = DoppeActionContentSection.Common & {
        layoutSettings: DoppeActionContentSectionLayoutSettings;
        title: string;
        isRequired: boolean;
        id: string;
    }

    export type Override = DoppeActionContentSection.Common & {
        useDefaultSectionContent: boolean;
    }

    export type ContentSectionWithOverride = DoppeActionContentSection.Section & DoppeActionContentSection.Override & {
        isDefaultSection: boolean;
    }


    type ContentBaseType<T extends DoppeActionContentSectionType> = {
        type: T;
    }

    export type RichTextSettings = ContentBaseType<typeof DoppeActionContentSectionType.RichContent> & {
        richText: RichTextContent
    };

    export type ImageSettings = ContentBaseType<typeof DoppeActionContentSectionType.Image> & {
        image: WixMediaResource | null;
        imageViewSettings: BymoPageActionMediaViewSettings;
    };

    export type VideoSettings = ContentBaseType<typeof DoppeActionContentSectionType.Video> & {
        video: DoppeBymoPageVideoSource | null;
        autoPlay: boolean;
    };

    export type GallerySettings = ContentBaseType<typeof DoppeActionContentSectionType.Gallery> &
        DoppeListActionSettings<DoppeActionTypeMediaGallery.MediaGalleryListItemSettings>  & {
    };

    export type TestimonialsSettings = ContentBaseType<typeof DoppeActionContentSectionType.Testimonials> &
        DoppeListActionSettings<DoppeActionTypeTestimonialsList.TestimonialItemSettings> & {

    }

    export type MapSettings = ContentBaseType<typeof DoppeActionContentSectionType.Map> & {
        address: {
            locationCoordinates: LocationCoordinates | null;
            displayAddress: HideableValue<string>;
        };
    };

    export type PropertyDetailsSettings = ContentBaseType<typeof DoppeActionContentSectionType.PropertyDetails> & {
        bedroomsNumber: HideableValue<number>;
        bathroomsNumber: HideableValue<number>;
        houseArea: HideableValue<string>;
        landArea: HideableValue<string>;
        floors: HideableValue<number>;
        yearBuilt: HideableValue<number>;
    };
}

export const DoppeActionContentSectionsGeneralSettingsMetadata = {
    title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    id: BymoPagePropsSpecialContentSettingMetadata.Ignored,
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionContentSection> | BymoPagePropsSpecialContentSettingMetadata;

export const DoppeActionContentSectionsSettingsMetadata = {
    [DoppeActionContentSectionType.RichContent]: {
        richText: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionContentSectionType.Image]: {
        image: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleMedia, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    },
    [DoppeActionContentSectionType.Video]: {
        video: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    },
    [DoppeActionContentSectionType.Gallery]: {
    },
    [DoppeActionContentSectionType.Testimonials]: {
    },
    [DoppeActionContentSectionType.Map]: {
    },
    [DoppeActionContentSectionType.PropertyDetails]: {
        bedroomsNumber: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
        bathroomsNumber: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
        houseArea: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        landArea: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
        floors: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
        yearBuilt: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    },
} satisfies {
    [SECTION in DoppeActionContentSectionContent as SECTION['type']]: BymoPagePropsContentSettingsMetadata<OmitStrict<SECTION, 'type'>> | BymoPagePropsSpecialContentSettingMetadata;
}

export const DoppeActionContentSectionsHideableSettingsNames: Partial<Record<DoppeActionContentSectionType, Record<string, true>>> = {
    [DoppeActionContentSectionType.PropertyDetails]: { houseArea: true, landArea: true },
};

export const DoppeActionContentSectionLayoutType = {
    Inline: 'Inline',
    Collapsable: 'Collapsable',
} as const;

export type DoppeActionContentSectionLayoutType = Values<typeof DoppeActionContentSectionLayoutType>;

export type DoppeActionContentSectionLayoutSettings =
    | DoppeActionContentSectionLayoutSettings.Inline
    | DoppeActionContentSectionLayoutSettings.Collapsable;

export namespace DoppeActionContentSectionLayoutSettings {

    type BaseType<T extends DoppeActionContentSectionLayoutType> = {
        type: T;
    }

    export type Inline = BaseType<typeof DoppeActionContentSectionLayoutType.Inline> & {
        hideHeader: boolean;
    };

    export type Collapsable = BaseType<typeof DoppeActionContentSectionLayoutType.Collapsable> & {
        isOpenByDefault: boolean;
    };
}

